import React, { useCallback, useEffect, useState } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Box, Avatar,
  CircularProgress,
} from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useAuth } from "../auth/AuthContext";
import {
  clearAppSettings, readAppSettings, writeAppSettings,
  apiGet, apiPost, ApiError,
} from "../api/client";
import { useToast } from "../ui/Toaster";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { success, info, error: toastError } = useToast();

  // Legacy local creds (only used by chrome extension fallback). Most users
  // won't need to touch these — they're optional.
  const initial = readAppSettings();
  const [legacyUser, setLegacyUser] = useState(initial.username || "");
  const [legacyPwd, setLegacyPwd] = useState(initial.password || "");

  const saveLegacy = (e) => {
    e?.preventDefault?.();
    writeAppSettings({ username: legacyUser, password: legacyPwd });
    success("Saved", "Legacy credentials updated.");
  };
  const clearLegacy = () => {
    clearAppSettings();
    setLegacyUser("");
    setLegacyPwd("");
    info("Cleared", "Legacy credentials removed from this device.");
  };

  // Shipping origin (warehouse) — read/write via /api/shipping_origin.
  // Used by the Buy-Label flow on unfulfilled sales. eBay Logistics
  // requires a valid from-address for every quote.
  const [origin, setOrigin] = useState({
    full_name: "", company_name: "", address1: "", address2: "",
    city: "", state: "", postal_code: "", country: "US", phone: "",
  });
  const [originLoading, setOriginLoading] = useState(true);
  const [originSaving, setOriginSaving]   = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet("/api/shipping_origin");
        if (!cancelled && data) setOrigin((o) => ({ ...o, ...data }));
      } catch (e) {
        if (!cancelled) toastError("Couldn't load shipping origin",
          e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setOriginLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toastError]);

  const setO = (k, v) => setOrigin((o) => ({ ...o, [k]: v }));

  const saveOrigin = async (e) => {
    e?.preventDefault?.();
    setOriginSaving(true);
    try {
      const data = await apiPost("/api/shipping_origin", origin);
      if (data?.origin) setOrigin((o) => ({ ...o, ...data.origin }));
      success("Saved", "Shipping origin updated.");
    } catch (e) {
      toastError("Save failed", e instanceof ApiError ? e.message : String(e));
    } finally {
      setOriginSaving(false);
    }
  };

  // eBay Seller Hub cookie status — populated by the chrome extension.
  // We poll on focus so when the operator re-syncs from the extension
  // and clicks back to the tab the indicator updates without a refresh.
  const [cookieStatus, setCookieStatus] = useState(null);
  const [cookieLoading, setCookieLoading] = useState(true);
  const refreshCookieStatus = useCallback(async () => {
    try {
      const data = await apiGet("/api/ebay/session_cookies/status");
      setCookieStatus(data || null);
    } catch {
      setCookieStatus(null);
    } finally {
      setCookieLoading(false);
    }
  }, []);
  useEffect(() => {
    refreshCookieStatus();
    const onFocus = () => {
      if (document.visibilityState === "visible") refreshCookieStatus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshCookieStatus]);

  // eBay cookies are good for hours, not days — call anything older
  // than ~4 hours "stale" so the operator gets a nudge to re-sync
  // before clicking Buy label and getting a 428.
  const cookieAgeMin = cookieStatus?.age_seconds != null
    ? Math.floor(cookieStatus.age_seconds / 60) : null;
  const cookieFreshness =
    !cookieStatus?.present       ? 'missing' :
    cookieAgeMin == null         ? 'unknown' :
    cookieAgeMin < 60            ? 'fresh'   :
    cookieAgeMin < 240           ? 'aging'   :
                                   'stale';

  return (
    <Box sx={{ maxWidth: 720 }}>
      {/* Page header — matches OC's standard layout (h1 serif title + secondary subtitle) */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Settings
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            Account and dashboard preferences
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2.5}>
        {/* Account section */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h3" sx={{ mb: 0.5 }}>Account</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You're signed in to the operator console.
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'primary.main',
                fontSize: '1.125rem',
                fontWeight: 700,
              }}
            >
              {(user?.username || "?").charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.username || "(unknown)"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Session is stored in an HttpOnly cookie.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LogoutOutlinedIcon />}
              onClick={logout}
            >
              Sign out
            </Button>
          </Stack>
        </Paper>

        {/* eBay Seller Hub session — populated by the chrome extension.
            The extension reads cookies from www.ebay.com and POSTs them
            to /api/ebay/session_cookies. We just show the freshness
            indicator here so the operator knows when to re-sync. */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <LocalShippingOutlinedIcon fontSize="small" />
            <Typography variant="h3">eBay Seller Hub session</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The Buy-Label flow on Unfulfilled Sales calls the eBay Seller
            Hub label service directly, which needs your browser cookies.
            Sync them from the BidRL chrome extension whenever they get
            stale (every ~4 hours or after you sign back into eBay).
          </Typography>
          {cookieLoading ? (
            <Stack direction="row" spacing={1} alignItems="center"
              sx={{ color: "text.secondary" }}>
              <CircularProgress size={14} />
              <Typography variant="body2">Loading…</Typography>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              {cookieFreshness === 'fresh' && (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'success.lighter', color: 'success.dark',
                  border: 1, borderColor: 'success.light',
                  px: 1.25, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 600,
                }}>
                  ● Fresh ({cookieAgeMin} min old)
                </Box>
              )}
              {cookieFreshness === 'aging' && (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'warning.lighter', color: 'warning.dark',
                  border: 1, borderColor: 'warning.light',
                  px: 1.25, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 600,
                }}>
                  ● Aging ({Math.floor(cookieAgeMin/60)}h {cookieAgeMin%60}m old)
                </Box>
              )}
              {cookieFreshness === 'stale' && (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'error.lighter', color: 'error.dark',
                  border: 1, borderColor: 'error.light',
                  px: 1.25, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 600,
                }}>
                  ● Stale (re-sync from extension)
                </Box>
              )}
              {cookieFreshness === 'missing' && (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'error.lighter', color: 'error.dark',
                  border: 1, borderColor: 'error.light',
                  px: 1.25, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 600,
                }}>
                  ● Not synced — click extension in your browser
                </Box>
              )}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {cookieStatus?.cookie_count
                  ? `${cookieStatus.cookie_count} cookies stored`
                  : 'No cookies on file'}
                {cookieStatus?.updated_at && ` · last updated ${new Date(cookieStatus.updated_at).toLocaleString()}`}
              </Typography>
              <Button size="small" variant="outlined" onClick={refreshCookieStatus}>
                Recheck
              </Button>
            </Stack>
          )}
        </Paper>

        {/* Shipping origin — feeds the Buy-Label flow on Unfulfilled Sales.
            eBay's Logistics API needs a valid from-address for every rate
            quote; this is the single warehouse address used. */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <LocalShippingOutlinedIcon fontSize="small" />
            <Typography variant="h3">Shipping origin</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Where eBay shipping labels are sent from. Used every time you
            buy a label from the Unfulfilled Sales page.
          </Typography>
          {originLoading ? (
            <Stack direction="row" spacing={1} alignItems="center"
              sx={{ color: "text.secondary" }}>
              <CircularProgress size={14} />
              <Typography variant="body2">Loading…</Typography>
            </Stack>
          ) : (
            <Box component="form" onSubmit={saveOrigin}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField label="Sender name" size="small" fullWidth
                    value={origin.full_name}
                    onChange={(e) => setO("full_name", e.target.value)} />
                  <TextField label="Company (optional)" size="small" fullWidth
                    value={origin.company_name}
                    onChange={(e) => setO("company_name", e.target.value)} />
                </Stack>
                <TextField label="Address line 1" size="small" fullWidth required
                  value={origin.address1}
                  onChange={(e) => setO("address1", e.target.value)} />
                <TextField label="Address line 2 (optional)" size="small" fullWidth
                  value={origin.address2}
                  onChange={(e) => setO("address2", e.target.value)} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField label="City" size="small" fullWidth required
                    value={origin.city}
                    onChange={(e) => setO("city", e.target.value)} />
                  <TextField label="State" size="small" sx={{ width: { sm: 120 } }} required
                    value={origin.state}
                    onChange={(e) => setO("state", e.target.value.toUpperCase())} />
                  <TextField label="ZIP" size="small" sx={{ width: { sm: 140 } }} required
                    value={origin.postal_code}
                    onChange={(e) => setO("postal_code", e.target.value)} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField label="Country" size="small" sx={{ width: { sm: 140 } }}
                    value={origin.country}
                    onChange={(e) => setO("country", e.target.value.toUpperCase())} />
                  <TextField label="Phone (recommended for UPS/FedEx)" size="small" fullWidth
                    value={origin.phone}
                    onChange={(e) => setO("phone", e.target.value)} />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" disabled={originSaving}
                    startIcon={originSaving ? <CircularProgress size={14} color="inherit" /> : null}>
                    {originSaving ? "Saving…" : "Save"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Browser-extension credentials section */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h3" sx={{ mb: 0.5 }}>
            Browser-extension credentials (optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Used only by the legacy chrome extension. If you don't use the extension,
            leave these empty.
          </Typography>
          <Box component="form" onSubmit={saveLegacy}>
            <Stack spacing={2}>
              <TextField
                id="legacy-username"
                label="Username"
                size="small"
                fullWidth
                autoComplete="off"
                value={legacyUser}
                onChange={(e) => setLegacyUser(e.target.value)}
              />
              <TextField
                id="legacy-password"
                label="Password"
                type="password"
                size="small"
                fullWidth
                autoComplete="new-password"
                value={legacyPwd}
                onChange={(e) => setLegacyPwd(e.target.value)}
              />
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained">Save</Button>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={clearLegacy}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
